/**
 * Le registre des droits qu'une agence peut ouvrir à un accès client.
 *
 * Une seule liste pour tous les écrans concernés : la fiche d'un accès client, la colonne
 * « Droits » de la liste, l'ouverture de la vue Outils et le filtrage du catalogue. Son
 * pendant serveur est `backend/src/constants/clientTools.js`, qui fait autorité sur
 * l'accordabilité — l'identifiant est celui du registre d'outils de `ToolsView.js`.
 *
 * ⚠️ **Ajouter un droit, c'est ajouter une entrée ici, et rien d'autre.** La fiche dérive ses
 * sections de `FAMILLES_DROITS` et le composant de chaque ligne de sa `portee` : une mise en
 * page qui connaîtrait un identifiant en dur redériverait vers des cas particuliers au
 * troisième droit ajouté. `backend/scripts/test-registre-droits-client.js` le vérifie, et
 * c'est le contrôle qui tient la promesse.
 *
 * C'est aussi la raison d'être de ce registre : l'onglet « Accès clients » posait une colonne
 * de case à cocher par outil, soit 1512 px de largeur minimale à six outils, et 132 px de plus
 * par droit ajouté. La liste ne porte plus qu'un résumé ; le détail vit dans la fiche.
 */

/**
 * Les familles, dans l'ordre où la fiche les présente.
 *
 * Elles répondent à « de quoi parle ce droit ? », pas à « quelle table le porte ? » : deux
 * droits de la même famille peuvent avoir des portées différentes, et c'est le cas dès que les
 * dashboards gagnent leurs réglages par carte.
 */
export const FAMILLES_DROITS = [
  {
    id: 'dashboards',
    nom: 'Dashboards',
    description: "Ce que le client voit des tableaux de bord de ses comptes."
  },
  {
    id: 'outils',
    nom: 'Outils',
    description: "Les outils que le client ouvre depuis la boîte à outils, en consultation."
  },
  {
    id: 'ia',
    nom: 'IA et connecteurs',
    description: "L'analyse automatique et l'accès par le connecteur Claude."
  }
];

/**
 * La **portée** d'un droit : ce sur quoi il se règle.
 *
 * ⚠️ C'est elle, et elle seule, qui décide du composant rendu par la fiche (table
 * `CONTROLES_PAR_PORTEE`). Un droit d'une portée déclarée s'affiche sans qu'on touche à la
 * mise en page ; une portée inconnue échoue au contrôle, pas à l'écran.
 */
export const PORTEES = {
  /** Un booléen par (organisation, email) — porté par `ClientToolAccess`. */
  ORGANISATION: 'organisation',
  /** Une liste par carte cliente — porté par `ClientDashboardConfig`. */
  PAR_CARTE: 'par-carte',
  /** Une liste par fiche de dashboard — porté par `DashboardSurMesure.visibleClient`. */
  PAR_DASHBOARD: 'par-dashboard',
  /**
   * Une liste par plateforme — portée par `ClientDashboardAccess`.
   *
   * ⚠️ **C'est la seule portée dont le défaut est ACCORDÉ.** Les autres ne donnent rien tant
   * que l'agence n'a rien coché ; celle-ci gouverne des écrans visibles depuis toujours, et
   * refuser par défaut les fermerait tous le jour de la mise en production. Une ligne en base
   * n'existe donc que pour un dashboard **retiré**.
   */
  PAR_PLATEFORME: 'par-plateforme'
};

const PORTEES_CONNUES = Object.values(PORTEES);

/**
 * Les droits accordables.
 *
 * Champs :
 * - `id`          l'identifiant du registre d'outils ; c'est lui qui voyage jusqu'au serveur
 * - `famille`     une entrée de `FAMILLES_DROITS`
 * - `portee`      une valeur de `PORTEES` — décide du composant rendu
 * - `nom`         le libellé complet, celui de la fiche
 * - `nomCourt`    pour les endroits étroits : une pastille, une bulle, un filtre
 * - `description` une phrase, servie par la bulle au survol. ⚠️ Jamais un `title` natif :
 *                 il ne sort pas sur une cellule de liste
 * - `ouvrePar`    où le client le trouve. C'est la première question que pose une agence —
 *                 « je lui ai donné le droit, où est-ce qu'il clique ? »
 * - `suitUsage`   le serveur rend un verdict « utilisé » pour les clients rattachés, d'où la
 *                 pastille « accordé mais inutilisé »
 * - `siInutilise` ⚠️ **ce qu'il faut vérifier quand ce verdict est négatif.** Le message
 *                 générique — « n'est utilisé pour aucun des clients auxquels il est
 *                 rattaché » — envoie chercher du côté du rattachement, alors que la cause est
 *                 presque toujours ailleurs et propre à l'outil. Vécu : « Dashboards sur
 *                 mesure » restait inutilisé parce qu'aucune fiche n'était marquée visible par
 *                 le client, et l'on a cherché le rattachement, puis un verrou imaginaire
 * - `dansOutils`  `false` pour ce qui a son entrée propre dans la navigation
 */
/*
 * ⚠️ **Tout ce qui s'accorde ici s'ouvre en consultation seule**, sans exception : un accès
 * client ne modifie jamais rien. Un seul droit le portait en étiquette — l'Optimiseur de flux,
 * parce que c'est le seul dont le serveur a dû se donner une porte d'écriture explicite — et
 * cette étiquette disait le contraire de ce qu'elle voulait dire : une ligne marquée
 * « consultation seule » laisse entendre que les autres ne le sont pas.
 */
export const DROITS_CLIENT = [
  {
    id: 'competitor-ads',
    famille: 'outils',
    portee: PORTEES.ORGANISATION,
    nom: 'Veille concurrentielle Social Ads',
    // Libellé court pour les endroits étroits : le nom complet y tiendrait mal.
    nomCourt: 'Veille Social Ads',
    description: "Les annonces des concurrents suivis pour les clients auxquels il est "
      + "rattaché, avec leur historique.",
    ouvrePar: 'Boîte à outils',
    siInutilise: "Aucun concurrent n'est suivi pour les clients auxquels il est rattaché.",
    suitUsage: true
  },
  {
    id: 'feed-optimizer',
    famille: 'outils',
    portee: PORTEES.ORGANISATION,
    nom: 'Optimiseur de flux',
    nomCourt: 'Optimiseur',
    description: "L'analyse de ses flux produits et les suggestions déjà générées. Il ne peut "
      + "ni générer, ni valider, ni modifier.",
    ouvrePar: 'Boîte à outils',
    siInutilise: "Aucun de leurs comptes Merchant Center n'a de flux analysé.",
    suitUsage: true
    // Il a bien sa place dans la boîte à outils : c'est une destination du catalogue, au
    // même titre que la veille concurrentielle — contrairement à quatre des six droits.
  },
  {
    id: 'product-labeling',
    famille: 'outils',
    portee: PORTEES.ORGANISATION,
    nom: 'Étiquetage de produits',
    nomCourt: 'Étiquetage',
    description: "Le classement de ses produits par ventes et par ROAS, les étiquettes "
      + "appliquées et le flux servi à Merchant Center. Il ne peut ni régler les seuils, ni "
      + "étiqueter à la main, ni régénérer le flux.",
    ouvrePar: 'Boîte à outils',
    siInutilise: "Aucun de leurs comptes Google Ads n'a d'étiquetage configuré.",
    suitUsage: true
    // ⚠️ Un compte **Google Ads**, et non Merchant : l'outil classe d'après les ventes et le
    // ROAS, qui vivent dans la régie. C'est la confusion naturelle avec l'Optimiseur de flux,
    // et elle rendrait le verdict d'usage toujours faux.
  },
  {
    id: 'dashboards-plateforme',
    famille: 'dashboards',
    portee: PORTEES.PAR_PLATEFORME,
    nom: 'Dashboards par plateforme',
    nomCourt: 'Plateformes',
    description: "Le dashboard de chaque régie et de chaque solution de mesure rattachée. "
      + "Tout est ouvert par défaut : ne décochez que ce que vous voulez lui fermer.",
    ouvrePar: 'Navigation',
    // Pas de verdict d'usage : la liste ne montre que les plateformes réellement présentes
    // chez les clients rattachés, donc il n'y a jamais de ligne « accordée mais inutilisée ».
    suitUsage: false,
    dansOutils: false
  },
  {
    id: 'consolidated-ga4',
    famille: 'dashboards',
    portee: PORTEES.ORGANISATION,
    nom: 'Dashboard consolidé',
    nomCourt: 'Consolidé',
    description: "Le croisement de ses régies avec sa solution de mesure. Demande une source "
      + "de mesure et au moins une régie sur un même client.",
    ouvrePar: 'Navigation',
    siInutilise: "Il faut une solution de mesure ET une régie sur un même client : le consolidé croise les"
      + " deux.",
    suitUsage: true,
    // Accordable comme un outil, mais ce n'en est pas un : il a son entrée propre dans la
    // navigation, et la liste des Outils l'affichait en doublon — un titre qui n'ouvrait rien
    // du catalogue et menait ailleurs que là où il était rangé.
    dansOutils: false
  },
  {
    id: 'dashboard-sur-mesure',
    famille: 'dashboards',
    portee: PORTEES.ORGANISATION,
    /*
     * ⚠️ **Le seul droit à deux crans**, et `sousReglages` est ce qui le dit.
     *
     * `ClientToolAccess` ouvre l'écran — c'est la case, donc la portée reste `organisation` —
     * et `DashboardSurMesure.visibleClient` ouvre chaque fiche. Les deux se règlent désormais
     * ici ; le second vivait dans un autre écran, où l'on arrivait après avoir lu « inutilisé »
     * sans savoir où aller.
     *
     * ⚠️ **Un champ à part de `portee`, et non à sa place.** La portée dit comment le droit
     * lui-même se stocke, et c'est elle que l'invariant inter-langages compare au catalogue
     * serveur. La changer ferait disparaître ce droit de l'invariant — alors qu'il est bien
     * un `ClientToolAccess` que le serveur arbitre.
     */
    sousReglages: PORTEES.PAR_DASHBOARD,
    nom: 'Dashboards sur mesure',
    nomCourt: 'Sur mesure',
    description: "Les dashboards sur mesure de ses comptes. ⚠️ Ce droit ouvre l'écran ; chaque "
      + "fiche doit en plus être marquée visible par le client.",
    ouvrePar: 'Navigation Analytics',
    siInutilise: "Aucun dashboard n'est marqué « visible par le client ». Ce marquage se fait dans la moda"
      + "le des dashboards sur mesure, et il est distinct de ce droit — il en faut les deux.",
    suitUsage: true,
    // Même cas que le consolidé : son entrée est dans la navigation Analytics, où elle
    // s'ouvre depuis l'écran de choix du dashboard. La boîte à outils l'afficherait en doublon.
    dansOutils: false
  },
  {
    id: 'mcp',
    famille: 'ia',
    portee: PORTEES.ORGANISATION,
    nom: 'Serveur MCP (Claude)',
    nomCourt: 'MCP',
    description: "L'interrogation de ses données depuis Claude, en lecture seule. Demande un "
      + "plan Agence côté agence.",
    ouvrePar: 'Connecteur Claude',
    siInutilise: "Aucun de leurs comptes n'est d'un type que le connecteur sait lire.",
    suitUsage: true,
    // Même cas que le consolidé : il a son entrée propre dans la navigation, la boîte à
    // outils l'afficherait en doublon.
    dansOutils: false
  },
  {
    id: 'analyse-ia',
    famille: 'ia',
    portee: PORTEES.ORGANISATION,
    nom: 'Analyse IA des dashboards',
    nomCourt: 'Analyse IA',
    description: "Le bouton d'analyse automatique posé sur ses dashboards.",
    ouvrePar: 'Bouton sur chaque dashboard',
    siInutilise: "Aucun compte n'est rattaché à leurs clients.",
    suitUsage: true,
    // Ce n'est pas un écran mais un bouton posé sur chaque dashboard : il n'a rien à faire
    // dans la boîte à outils, qui ne liste que des destinations.
    dansOutils: false
  }
];

/**
 * Les droits de portée « organisation », c'est-à-dire ceux que `ClientToolAccess` porte.
 *
 * Le nom historique est conservé : c'est sous celui-là que `App.js` et l'onglet « Accès
 * clients » l'importent, et les six droits actuels sont tous de cette portée. Les portées
 * `par-carte` et `par-dashboard` ne se comptent pas en cases à cocher et n'ont donc rien à
 * faire ici — ni dans le dénominateur du « 3 / 6 » de la liste.
 */
export const CLIENT_TOOLS = DROITS_CLIENT.filter(droit => droit.portee === PORTEES.ORGANISATION);

export const CLIENT_TOOL_IDS = CLIENT_TOOLS.map(outil => outil.id);

/**
 * Ceux qui figurent dans la vue Outils.
 *
 * La fiche garde la liste entière : ce qui s'accorde et ce qui se range dans les Outils sont
 * deux questions distinctes, et les confondre a mis le dashboard consolidé dans une boîte à
 * outils où il n'a rien à faire.
 */
export const CLIENT_TOOLS_CATALOGUE = CLIENT_TOOLS.filter(outil => outil.dansOutils !== false);

/**
 * Parmi des droits accordés, ceux qui ouvrent réellement quelque chose dans la vue Outils.
 *
 * À employer partout où l'on décide d'afficher l'onglet « Outils ». Compter les droits bruts
 * y ouvre un onglet vide dès que les seuls accordés sont hors catalogue — la sidebar, elle,
 * filtre bien sur le catalogue, si bien que le bouton mène à une liste sans rien dedans.
 */
export const outilsDuCatalogue = (ids = []) => (
  ids.filter(id => CLIENT_TOOLS_CATALOGUE.some(outil => outil.id === id))
);

/** Les droits d'une famille, dans l'ordre du registre. */
export const droitsDeLaFamille = (familleId) => (
  DROITS_CLIENT.filter(droit => droit.famille === familleId)
);

/** Un droit par son identifiant, ou `undefined`. */
export const droitParId = (id) => DROITS_CLIENT.find(droit => droit.id === id);

/**
 * Les libellés d'une liste d'identifiants, dans l'ordre du registre.
 *
 * ⚠️ Un identifiant inconnu **garde sa valeur brute** plutôt que de disparaître : une liste
 * qui s'ampute en silence se lit comme un droit retiré. Le cas arrive quand le serveur porte
 * un droit que le navigateur ne connaît pas encore — un déploiement en deux temps.
 */
export const libellesDesDroits = (ids = []) => (
  DROITS_CLIENT.filter(droit => ids.includes(droit.id)).map(droit => droit.nom)
    .concat(ids.filter(id => !droitParId(id)))
);

/**
 * ⚠️ Refuse au chargement une entrée mal formée.
 *
 * Même parti que `verifierAliases()` et `verifierDecodeursEnum()` côté serveur : un droit
 * déclaré avec une famille ou une portée inexistante ne casserait rien — la fiche
 * n'afficherait simplement pas sa section, ou rendrait `undefined` comme composant. Mieux
 * vaut échouer à l'import que laisser un droit invisible.
 */
const verifierRegistre = () => {
  const familles = FAMILLES_DROITS.map(f => f.id);
  const fautes = [];

  DROITS_CLIENT.forEach(droit => {
    if (!familles.includes(droit.famille)) {
      fautes.push(`« ${droit.id} » déclare la famille « ${droit.famille} », qui n'existe pas`);
    }
    if (!PORTEES_CONNUES.includes(droit.portee)) {
      fautes.push(`« ${droit.id} » déclare la portée « ${droit.portee} », qui n'existe pas`);
    }
    if (!droit.nom || !droit.description) {
      fautes.push(`« ${droit.id} » doit porter un nom et une description`);
    }
  });

  const ids = DROITS_CLIENT.map(d => d.id);
  ids.forEach((id, i) => {
    if (ids.indexOf(id) !== i) fautes.push(`« ${id} » est déclaré deux fois`);
  });

  if (fautes.length) {
    throw new Error(`Registre des droits client mal formé :\n  - ${fautes.join('\n  - ')}`);
  }
};

verifierRegistre();
